'use client';

import { useState } from 'react';

import { Chip } from '@gds/Chip/Chip';
import { Typography } from '@gds/Typography/Typography';
import type { ReviewPage } from '@umbraco/Entities/ReviewEntities.types';
import { interpolateContent } from '@umbraco/Utils/InterpolateContent';
import { mapMediaImages } from '@umbraco/Utils/MapMediaImages';

import { ReviewCarouselTile } from '../ReviewCarouselTile/ReviewCarouselTile';

import styles from './MakeReviews.module.css';

const ALL = 'All';

type MakeReviewsChip = { name: string; codename: string };

type MakeReviewsStrings = {
  reviewCarouselStrs: Record<string, string>;
  otherStrs: { browseBy: string; allTab: string; allMakeCars: string };
};

type MakeReviewsProps = {
  reviews: ReviewPage[];
  taxonomies: MakeReviewsChip[];
  strs: MakeReviewsStrings;
};

const hasTaxonomy = (review: ReviewPage, name: string) =>
  (review.properties.taxonomy ?? []).some(taxonomy => taxonomy.name?.trim() === name);

export const MakeReviews = ({ reviews, taxonomies, strs }: MakeReviewsProps) => {
  const [selectedTaxonomy, setSelectedTaxonomy] = useState<string>(ALL);

  const filteredReviews =
    selectedTaxonomy === ALL
      ? reviews
      : reviews.filter(review => hasTaxonomy(review, selectedTaxonomy));

  return (
    <>
      <Typography Component="h2" variant="h5" className={styles.browseBy}>
        {strs.otherStrs.browseBy}
      </Typography>
      <section className={styles.chipFilters}>
        <Chip selected={selectedTaxonomy === ALL} onClick={() => setSelectedTaxonomy(ALL)}>
          {strs.otherStrs.allTab}
        </Chip>
        {taxonomies.map(taxonomy => (
          <Chip
            key={taxonomy.codename}
            selected={selectedTaxonomy === taxonomy.name}
            onClick={() => setSelectedTaxonomy(taxonomy.name)}
          >
            {taxonomy.name}
          </Chip>
        ))}
      </section>
      <section className={styles.makeTiles}>
        <Typography Component="h2" variant="h3">
          {strs.otherStrs.allMakeCars}
        </Typography>
        <ul className={styles.cards}>
          {filteredReviews.map(review => {
            if (!review.route.path) return null;
            const imgGallery = mapMediaImages(review.properties.imageGallery ?? []);

            return (
              <li key={review.id} className={styles.reviews}>
                <ReviewCarouselTile
                  slug={review.route.path}
                  imgGallery={imgGallery}
                  title={interpolateContent(review.properties.seoMetadataTitle ?? review.name)}
                  subTitle={interpolateContent(review.properties.standfirst ?? '')}
                  strs={strs.reviewCarouselStrs}
                />
              </li>
            );
          })}
        </ul>
      </section>
    </>
  );
};
